/* eslint-disable @typescript-eslint/ban-types */
import { validator } from "./validator";
import { getFieldName, isMandatoryVideoNode } from "./templateUtils";
import { NodeType, QuestionType } from "./templateTypes";
import { explodeFieldArray } from "./BlockFieldArray.helpers";
import { jsonLogic } from "./json-logic";
import { RESIDENT_DEMOGRAPHICS_RULES } from "./rules";
/**
 * Checks if a block is a form control block.
 *
 * @param {Blocks} block The block to check.
 * @returns {boolean} Whether the block is a form control block.
 */
export const isFormControlBlock = (block) => block.type === NodeType.FORM_CONTROL;
/**
 * Gets the name of a block.
 *
 * @param {FormFieldBlocks} block The block to get the name of.
 * @returns {string} The name of the block.
 */
export const getBlockName = (block) => isFormControlBlock(block) || isMandatoryVideoNode(block)
    ? getFieldName(block.reference)
    : getFieldName(block);
const parseJsonLogicRule = (rule, values) => {
    const [ruleName] = Object.keys(rule);
    return jsonLogic.apply(rule[ruleName], values) ? ruleName : null;
};
export const parseJsonLogicRuleForBlockField = (rule, values, fieldName) => {
    const [ruleName] = Object.keys(rule);
    return jsonLogic.apply(JSON.parse(JSON.stringify(rule[ruleName]).replace(/\.\*\./g, `.${fieldName.split(".")[1]}.`)), values)
        ? ruleName
        : null;
};
/**
 * Reduces list of fields to Rules record mapping field name to list of validation rules.
 *
 * @param {ReadonlyArray<FormFieldBlocks>} fields The fields to get the validation rules for.
 * @param {Values} [values] The values to use for json-logic rules.
 * @returns {Rules} The validation rules.
 */
export const getValidationRules = (fields, values) => fields.reduce((rules, field) => {
    if (field.type === NodeType.DEMOGRAPHICS) {
        return {
            ...rules,
            ...RESIDENT_DEMOGRAPHICS_RULES,
        };
    }
    const blockName = getBlockName(field);
    const search = /([\w]+)\.([0-9]+)\.([\w]+)/;
    return {
        ...rules,
        [blockName]: field.rules
            .map(rule => typeof rule === "object"
            ? search.test(blockName)
                ? parseJsonLogicRuleForBlockField(rule, values, blockName)
                : parseJsonLogicRule(rule, values)
            : rule)
            .filter(rule => rule !== null),
    };
}, {});
/**
 * Reduces list of fields to ErrorMessages record mapping rule&field name to error message.
 *
 * @param {ReadonlyArray<FormFieldBlocks>} fields The fields to get the validation messages for.
 * @returns {ErrorMessages} The validation messages.
 */
export const getValidationMessages = (fields) => fields.reduce((errorMessages, block) => {
    const fieldName = getBlockName(block);
    return {
        ...errorMessages,
        ...Object.keys(block.validationMessages ?? {}).reduce((messages, rule) => {
            messages[`${rule}.${fieldName}`] = block.validationMessages[rule];
            return messages;
        }, {}),
    };
}, {});
/**
 * Return true when block is field array, form control, mandatory video or demographics.
 */
const isFormBlock = (block) => block.type === NodeType.FORM_CONTROL ||
    block.type === NodeType.FIELD_ARRAY ||
    block.type === NodeType.MANDATORY_VIDEO ||
    block.type === NodeType.DEMOGRAPHICS;
/**
 * Add current block if it's a form block
 * @param block
 * @param prefix
 */
const addCurrentBlock = (block, prefix) => block.reference
    ? [
        {
            ...block,
            reference: {
                ...block.reference,
                id: `${prefix}${block.reference.id}`,
            },
        },
    ]
    : isFormBlock(block)
        ? [block]
        : [];
/**
 * Similar to 'extractFormFields' without extracting the fields from FieldArray children.
 */
export const extractSimpleFormFields = (children) => children.flatMap(block => {
    return [
        // Add current block if its a form block
        ...(isFormBlock(block) ? [block] : []),
        // Descend to choice children if possible
        ...(block.reference
            ? (block.reference.choices ?? [])
                .filter((choice) => choice.children)
                .flatMap((choice) => extractSimpleFormFields(choice.children))
            : []),
        // Descend to block children if possible
        ...(block.children &&
            block.type !== NodeType.FIELD_ARRAY &&
            block.type !== NodeType.MANDATORY_VIDEO
            ? extractSimpleFormFields(block.children)
            : // Include list blocks
                block.items
                    ? extractSimpleFormFields(block.items)
                    : []),
    ];
});
/**
 * Picks values for form fields, if value is not defined, supplies defaults.
 */
export const extractInitialValuesForFields = (fields, values) => {
    return fields.reduce((initialValues, field) => {
        const fieldName = getBlockName(field);
        if (!fieldName) {
            return initialValues;
        }
        if (field.type === NodeType.DEMOGRAPHICS) {
            initialValues[fieldName] = values[fieldName] || {
                resident_firstName: "",
                resident_lastName: "",
                resident_gender: "",
                resident_dateOfBirth: "",
                similar_resident: "NEW_RESIDENT",
            };
        }
        else if (field.type === NodeType.FIELD_ARRAY ||
            field.reference?.type === QuestionType.CHECKBOX) {
            initialValues[fieldName] = values[fieldName] || [];
        }
        else {
            initialValues[fieldName] = values[fieldName] || "";
        }
        return initialValues;
    }, {});
};
export const extractInitialFormValuesForBlock = ({ children, values = {}, }) => {
    const fields = extractSimpleFormFields(children).filter(isFormBlock);
    return extractInitialValuesForFields(fields, values);
};
/**
 * From the set of errors pick only those which are for visible form fields.
 * Purpose of this helper is to remove false-positive errors for FieldArray rules
 * as some as some conditional fields might be visible in one item and hidden in another...
 * @param errors - all form errors
 * @param blocks - current tree of blocks
 * @param values - current form values
 */
export const getErrorsForVisibleBlocks = (errors, blocks, values = {}) => {
    const fields = extractSimpleFormFields(blocks);
    const fieldArrayFields = fields.flatMap(field => {
        return field.type === NodeType.FIELD_ARRAY
            ? extractSimpleFormFields(explodeFieldArray(field, values[getFieldName(field)], values))
            : [];
    });
    return [...fields, ...fieldArrayFields]
        .filter(block => block.state.visible)
        .reduce((visibleErrors, block) => {
        const fieldName = getBlockName(block);
        // Validate fields that are nested under demographics
        Object.keys(errors).forEach(key => {
            if (block.type === NodeType.DEMOGRAPHICS) {
                visibleErrors[key] = errors[key];
            }
        });
        if (errors[fieldName]) {
            visibleErrors[fieldName] = errors[fieldName];
        }
        return visibleErrors;
    }, {});
};
/**
 * Descend to block children if possible
 * @param block
 * @param prefix
 * @param answers
 */
const descendToBlockChildren = (block, prefix, answers, extractFormFields) => block.children
    ? extractFormFields(block.children, prefix, answers)
    : // Include list blocks
        block.items
            ? extractFormFields(block.items, prefix, answers)
            : [];
/**
 * Descend to choice children if possible
 * @param block
 * @param prefix
 * @param answers
 */
const descendToChoiceChildren = (block, prefix, answers, extractFormFields) => block.reference
    ? (block.reference.choices ?? [])
        .filter((choice) => choice.children)
        .flatMap((choice) => extractFormFields(choice.children, prefix, answers))
    : [];
/**
 * Extracts list of all form fields from Blocks JSON tree.
 */
export const extractFormFields = (children, prefix = "", answers = {}) => children?.flatMap(block => block.type === NodeType.FIELD_ARRAY &&
    answers[getBlockName(block)]?.length > 0
    ? answers[getBlockName(block)]
        ?.map((_, index) => [
        ...addCurrentBlock(block, prefix),
        ...descendToChoiceChildren(block, prefix, answers, extractFormFields),
        ...descendToBlockChildren(block, `${block.id}.${index}.`, answers, extractFormFields),
    ])
        ?.flatMap(block => block)
        ?.filter((block, index, thisArg) => thisArg.findIndex(t => getBlockName(t) === getBlockName(block)) === index)
    : [
        ...addCurrentBlock(block, prefix),
        ...descendToChoiceChildren(block, prefix, answers, extractFormFields),
        ...descendToBlockChildren(block, "", answers, extractFormFields),
    ]);
/**
 * Create validation function together with rules & messages for a list of blocks.
 */
export const getValidationConfig = ({ children, answers, }) => {
    const fields = extractFormFields(children, undefined, answers).filter(field => field?.rules?.length && !field.state.disabled && field.state.visible);
    const validationRules = getValidationRules(fields, answers);
    const validationMessages = getValidationMessages(fields);
    const validate = (values, rules) => {
        const errors = validator.validateAll(values, rules ? { ...validationRules, ...rules } : validationRules, validationMessages);
        return getErrorsForVisibleBlocks(errors, children, values);
    };
    return {
        validate,
        validationRules,
        validationMessages,
    };
};
